/**
 * 운영자 세션.
 *
 * 트랑슈는 운영자 본인 계좌만 다루는 내부 도구라 신원이 하나뿐이고(`docs/SCOPE.md`),
 * 외부 IdP 를 쓰지 않아요. `POST /api/v1/auth/login` 이 비밀번호(+설정 시 TOTP)를 검증해
 * 세션 JWT 를 내려주고, 이 모듈이 그 토큰을 들고 있어요.
 *
 * 저장은 `localStorage` 예요. 처음에는 `sessionStorage` 를 썼는데, 탭을 닫거나 새 탭을 열
 * 때마다 로그인이 풀려서 운영자가 하루에 몇 번씩 다시 로그인해야 했어요.
 *
 * 저장 위치를 바꿔도 **토큰이 유효한 기간은 늘어나지 않아요** — 수명은 서버가 서명할 때
 * 박아 넣는 `exp`(기본 30일) 하나로 정해지고, `sessionStorage` 는 그 창을 줄여 주는 게
 * 아니라 브라우저가 토큰을 잊게 만들 뿐이었어요. 그래서 유효기간이라는 실제 방어선은
 * 그대로 두고, 기억은 브라우저에 맡겼어요. 여기서는 만료된 토큰을 미리 버려서
 * 불필요한 401 왕복만 줄여요.
 */

const STORAGE_KEY = "jupilot.session";

export interface Session {
  accessToken: string;
  /** epoch seconds */
  expiresAt: number;
  email: string;
}

let cached: Session | null | undefined;

function isLive(session: Session): boolean {
  // 30초 여유. 요청이 날아가는 동안 만료되는 경계를 피해요.
  return session.expiresAt - 30 > Date.now() / 1000;
}

function read(): Session | null {
  if (cached !== undefined) return cached;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cached = null;
      return null;
    }
    const parsed = JSON.parse(raw) as Session;
    cached =
      typeof parsed?.accessToken === "string" &&
      typeof parsed?.expiresAt === "number" &&
      isLive(parsed)
        ? parsed
        : null;
  } catch {
    // 저장된 값이 깨졌으면 로그아웃 상태로 다룰 뿐이고, 여기서 던지면 앱이 안 뜨게 돼요.
    cached = null;
  }
  if (cached === null) localStorage.removeItem(STORAGE_KEY);
  return cached;
}

export const session = {
  get(): Session | null {
    return read();
  },

  getToken(): string | null {
    return read()?.accessToken ?? null;
  },

  isAuthenticated(): boolean {
    return read() !== null;
  },

  save(input: { accessToken: string; expiresIn: number; email: string }): Session {
    const value: Session = {
      accessToken: input.accessToken,
      expiresAt: Math.floor(Date.now() / 1000) + input.expiresIn,
      email: input.email,
    };
    cached = value;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    return value;
  },

  clear(): void {
    cached = null;
    localStorage.removeItem(STORAGE_KEY);
  },
};
